
import React from 'react';
import clsx from 'clsx';

import { Logger } from 'wtb-utils';
import { Card, Radio, Typography } from 'wtb-components';
import { useLocaleContext, useTransactionContext } from 'context';
import { ACCOUNT_AUTH_TYPES, PAYMENT_TYPES } from 'constants.js';
// Payment methods
import { ReactComponent as AutogiroImage } from 'assets/images/autogiro/autogiro.svg';
import { ReactComponent as AvtalegiroImage } from 'assets/images/avtalegiro.svg';
import { ReactComponent as BankIDImage } from 'assets/images/bankid-color.svg';
import { ReactComponent as MastercardImage } from 'assets/images/credit-cards/mastercard.svg';
import { ReactComponent as SwishImage } from 'assets/images/swish/swish.svg';
import { ReactComponent as VisaImage } from 'assets/images/credit-cards/visa.svg';
import BetalingsserviceImage from 'assets/images/betalingsservice/betalingsservice.png';
import InvoiceImage from 'assets/images/invoice.png';
// Sign methods
import { ReactComponent as BankIdSignIcon } from 'assets/images/sign-methods/BankID.svg';
import { ReactComponent as MockBEBankIdSignIcon } from 'assets/images/sign-methods/MockBEBankID.svg';
import { ReactComponent as NordicSignIcon } from 'assets/images/sign-methods/Nordic.svg';
import { ReactComponent as MockNordicSignIcon } from 'assets/images/sign-methods/MockNordic.svg';
import styles from './PaymentPlan.module.css';

const isTest = [ 'test', 'dev' ].includes(process.env.REACT_APP_ENV);

const SignTypeIcon = ({ type }) => {
  switch (type) {
    case ACCOUNT_AUTH_TYPES.BANK:
      return <BankIdSignIcon />;
    case ACCOUNT_AUTH_TYPES.BANK_MOCK:
      return <MockBEBankIdSignIcon />;
    case ACCOUNT_AUTH_TYPES.AVTALEGIRO:
      return <NordicSignIcon />;
    case ACCOUNT_AUTH_TYPES.AVTALEGIRO_MOCK:
      return <MockNordicSignIcon />;
    default:
      return <></>;
  }
};

const PaymentPlanBody = ({ type, data }) => {
  const { order: { currency } } = useTransactionContext();
  const { translate } = useLocaleContext();

  let title;
  let body;
  let icons;

  switch (type) {
    case PAYMENT_TYPES.CARD: {
      title = translate('paymentPlans.card.title');
      icons = (
        <>
          <VisaImage className={styles.icon} aria-label="visa" />
          <MastercardImage className={styles.icon} aria-label="mastercard" />
        </>
      );

      break;
    }
    case PAYMENT_TYPES.INVOICE: {
      const { feeGrossValue, dueDays } = data;

      title = translate('paymentPlans.invoice.title');
      body = translate('paymentPlans.invoice.body', {
        amount: feeGrossValue,
        currency,
        period: dueDays
      });

      if (isTest && data?.signType !== ACCOUNT_AUTH_TYPES.NONE) {
        icons = <SignTypeIcon type={data.signType} />;
      } else {
        icons = <img className={styles.icon} src={InvoiceImage} aria-label={translate('paymentPlans.invoice.title')} />;
      }

      break;
    }
    case PAYMENT_TYPES.AUTOGIRO: {
      title = translate('paymentPlans.autogiro.title');
      icons = (
        <>
          {(isTest
            ? <SignTypeIcon type={data.signType} />
            : <BankIDImage className={styles.icon} aria-label="bankID" />
          )}
          <AutogiroImage className={styles.icon} aria-label="autogiro" />
        </>
      );

      break;
    }
    case PAYMENT_TYPES.BETALINGSSERVICE: {
      title = translate('paymentPlans.betalingsservice.title');
      icons = <img className={styles.icon} src={BetalingsserviceImage} aria-label={translate('paymentPlans.betalingsservice.title')} />;
      break;
    }
    case PAYMENT_TYPES.SEPA: {
      title = translate('paymentPlans.sepa.title');
      break;
    }
    case PAYMENT_TYPES.AVTALEGIRO_MOCK:
    case PAYMENT_TYPES.AVTALEGIRO: {
      title = translate('paymentPlans.avtalegiro.title');
      icons = (
        <AvtalegiroImage className={styles.icon} aria-label="avtalegiro" />
      );

      break;
    }
    case PAYMENT_TYPES.SWISH: {
      title = translate('paymentPlans.swish.title');
      icons = <SwishImage className={styles.icon} aria-label="swish" />;
      break;
    }
    default: {
      if (type !== 'PROFORMA') {
        Logger.error(`Payment type ${type} not available`);
      }
    }
  }

  return (
    <div className={styles.body}>
      <div className={styles.bodyTexts}>
        <Typography component="div" variant="subtitle1">{title}</Typography>
        {body && <Typography variant="body2" component="div">{body}</Typography>}
      </div>

      {icons && <div className={styles.icons}>{icons}</div>}
    </div>
  );
};

const PaymentPlan = (props) => {
  const {
    id,
    type,
    data = {},
    index
  } = props;

  const {
    selectedPaymentPlan,
    setSelectedPaymentPlan
  } = useTransactionContext();

  const handleChange = () => setSelectedPaymentPlan({ id, type, ...data });

  const hasPlanId = id !== undefined && id !== null;
  const isSelected = hasPlanId && selectedPaymentPlan?.id === id;

  const cardClassName = clsx(styles.paymentPlan, { [styles.selected]: isSelected });

  return (
    <Card
      className={cardClassName}
      id={type.toLowerCase()}
      role="button"
      tabIndex={typeof index === 'number' ? index + 1 : 0}
      onClick={handleChange}
      onKeyUp={handleChange}
      raised={false}
      aria-checked={isSelected}
      sign-type={data?.signType}
      pay-type={data?.payType}
    >
      <Radio
        className={styles.radio}
        checked={isSelected}
        aria-labelledby="plans"
      />
      <PaymentPlanBody type={type} data={data} />
    </Card>
  );
};

export default PaymentPlan;
